import type {
  JurisdictionWaivableFees,
  PermitFeesTotals,
} from '@contracts/order/ReviewableOrderResponse';
import type { BillingCurrency } from '@contracts/payment/Currency';
import type { TransactionCurrency } from '@contracts/payment/Currency';
import type { ExchangeRate } from '@contracts/payment/ExchangeRate';
import type { ClientSummary } from '@src/clients/domain/Client';
import type { Timestamp } from '@src/common/time/Timestamp';
import type { Billing } from '@src/orders/domain/Billing';
import { OrderStatus } from '@src/orders/domain/Order';
import { type OrderActions } from '@src/orders/domain/OrderList';
import type { PermitStatus } from '@src/orders/domain/PermitApplication';
import type { Transaction } from '@src/orders/domain/Transaction';
import type { DuplicationTrigger } from '@src/orders/hooks/useDuplicatePermitAsNew';

export type Attributes = {
  id: string;
  displayId: string;
  client: {
    name: string;
    doingBusinessAs?: string;
    invoicingInstructions?: string;
  };
  permitApplicationGroups: {
    originalPermit: ReviewablePermitApplication;
    subsequentPermits: SubsequentReviewablePermitApplication[];
  }[];
  status: OrderStatus;
  claimedBy?: {
    name: string;
  };
  tractor: {
    unit?: number;
    licensePlate?: string;
  };
  driver: {
    name?: string;
  };
  loadInformation: {
    serialNumber?: string;
  };
  additionalFees: AdditionalFee[];
  billTo: ClientSummary;
  billingCurrency?: BillingCurrency;
  billingDate?: string;
  exchangeRate: ExchangeRate;
  adminFeeRate: number;
  canEditAdminFeeRate: boolean;
  taxRate: number;
  billing?: Billing;
  permitFeesTotals: PermitFeesTotals;
  PONumber?: string;
  submittedDate: Timestamp;
  allowedActions: Set<OrderActions>;
};

interface WaivableFees {
  amount: number;
  isWaived: boolean;
  currency: TransactionCurrency;
}

export type ReviewablePermitApplication = {
  id: string;
  permitNumber: string;
  name: string;
  type: string;
  jurisdiction: string;
  status: PermitStatus;
  fees: {
    invoiceNumber?: string;
    jurisdictionFee: JurisdictionWaivableFees;
    serviceFee: WaivableFees;
  };
  isActive: boolean;
  currency: BillingCurrency;
};

export type SubsequentReviewablePermitApplication =
  ReviewablePermitApplication & {
    trigger: DuplicationTrigger;
  };

export type AdditionalFee = {
  id: string;
  amount: number;
  description: string;
  currency: string;
  taxable: boolean;
};

export class ReviewableOrder {
  public readonly id: string;
  public readonly displayId: string;
  public readonly client: {
    name: string;
    doingBusinessAs?: string;
    invoicingInstructions?: string;
  };
  public readonly permitApplicationGroups: {
    originalPermit: ReviewablePermitApplication;
    subsequentPermits: SubsequentReviewablePermitApplication[];
  }[];
  public readonly status: OrderStatus;
  public readonly claimedBy?: {
    name: string;
  };
  public readonly tractor: {
    unit?: number;
    licensePlate?: string;
  };
  public readonly driver: {
    name?: string;
  };
  public readonly loadInformation: {
    serialNumber?: string;
  };
  public readonly additionalFees: AdditionalFee[];
  public readonly billTo: ClientSummary;
  public readonly PONumber?: string;
  public readonly billingCurrency?: BillingCurrency;
  public readonly billingDate?: string;
  public readonly exchangeRate: ExchangeRate;
  public readonly adminFeeRate: number;
  public readonly taxRate: number;
  public readonly canEditAdminFeeRate: boolean;
  public readonly billing?: Billing;
  public readonly permitFeesTotals: PermitFeesTotals;
  public readonly submittedDate: Timestamp;
  public readonly allowedActions: Set<OrderActions>;

  constructor(attributes: Attributes) {
    this.id = attributes.id;
    this.displayId = attributes.displayId;
    this.client = attributes.client;
    this.permitApplicationGroups = attributes.permitApplicationGroups;
    this.status = attributes.status;
    this.claimedBy = attributes.claimedBy;
    this.tractor = attributes.tractor;
    this.driver = attributes.driver;
    this.billTo = attributes.billTo;
    this.billingCurrency = attributes.billingCurrency;
    this.billingDate = attributes.billingDate;
    this.loadInformation = attributes.loadInformation;
    this.additionalFees = attributes.additionalFees;
    this.PONumber = attributes.PONumber;
    this.exchangeRate = attributes.exchangeRate;
    this.adminFeeRate = attributes.adminFeeRate;
    this.canEditAdminFeeRate = attributes.canEditAdminFeeRate;
    this.taxRate = attributes.taxRate;
    this.billing = attributes.billing;
    this.permitFeesTotals = attributes.permitFeesTotals;
    this.submittedDate = attributes.submittedDate;
    this.allowedActions = attributes.allowedActions;
  }

  getPermitFeesTotals(): PermitFeesTotals {
    return this.permitFeesTotals;
  }

  getAllPermitFees(): {
    permitId: string;
    jurisdictionFee: JurisdictionWaivableFees;
    serviceFee: WaivableFees;
  }[] {
    const flattenPermits = this.getFlattenPermits();
    return flattenPermits.map((permit) => ({
      permitId: permit.id,
      ...permit.fees,
    }));
  }

  isInvoicing() {
    return this.status === OrderStatus.TO_INVOICE;
  }

  isInvoiced() {
    return this.status === OrderStatus.INVOICED;
  }

  isReviewing() {
    return (
      this.status === OrderStatus.TO_REVIEW ||
      this.status === OrderStatus.CANCELLED
    );
  }

  isCompleted() {
    return this.status === OrderStatus.COMPLETED;
  }

  isArchived() {
    return this.status === OrderStatus.ARCHIVED;
  }

  canContinue() {
    if (this.billing) {
      return !this.billing.hasDuePayment();
    }
    return false;
  }

  canChangeBillingCurrency(transactions: Transaction[] = []) {
    if (this.billTo.paymentMethod === 'LINE_OF_CREDIT') {
      return true;
    }
    if (this.billTo.paymentMethod === 'CREDIT_CARD') {
      return !transactions.some((transaction) =>
        transaction.isSuccessfullyCreatedBy(this.billTo.id),
      );
    }
  }

  isSameCurrency(otherCurrency: BillingCurrency): boolean {
    return otherCurrency == this.billingCurrency;
  }

  canGenerateInvoice(): boolean {
    return (
      this.isInvoicing() &&
      !this.permitApplicationGroups.some(
        (group) => group.originalPermit.fees.invoiceNumber,
      )
    );
  }

  isModifyingInvoice(): boolean {
    return this.status === OrderStatus.MODIFY_INVOICE;
  }

  hasNeededQuickbooksIds(): boolean {
    if (this.billingCurrency === 'CAD') {
      return !!this.billTo.quickbooksCADId?.length;
    } else if (this.billingCurrency === 'USD') {
      return !!this.billTo.quickbooksUSDId?.length;
    } else {
      return !!(
        this.billTo.quickbooksCADId?.length &&
        this.billTo.quickbooksUSDId?.length
      );
    }
  }

  private getFlattenPermits() {
    return this.permitApplicationGroups.flatMap((group) => [
      group.originalPermit,
      ...group.subsequentPermits,
    ]);
  }
}
